<template>
  <div class="p-2">
    <div>
      <DateQuerySetter btnTitle="Go" @onClickGo="onClickGo">
        <div class="col-12 col-sm-6 col-md-3 col-lg-2">
          <label class="col-form-label">Table</label>
          <AsyncSelect
              placeholder="Select Table"
              v-model="selectedTableId"
              :api-service="fetchRestaurantTablesDropdown"
              :reduce="option => option.id"
          />
        </div>
        <div class="col-12 col-sm-6 col-md-3 col-lg-2">
          <label class="col-form-label">Party</label>
          <AsyncSelect
              placeholder="Party Name, ID, Mobile No"
              :api-service="fetchContactProfiles"
              :reduce="name => name.id"
              v-model="contactProfileId"
              :additional-query="{ type: 'customer' }"
              :format-label="formatPatientLabel"
          />
        </div>
        <div class="col-12 col-sm-6 col-md-3 col-lg-2">
          <label class="col-form-label">Status</label>
          <v-select
              placeholder="Select Status"
              v-model="selectedStatus"
              :options="orderStatuses"
              label="name"
              :reduce="option => option.value"
          />
        </div>
      </DateQuerySetter>
    </div>

    <div class="d-flex justify-content-end mb-1 mt-1">
      <button class="btn btn-primary btn-sm" @click="$emit('addOrder')">
        <i class="fas fa-plus mr-1"></i> Add New Order
      </button>
    </div>

    <div class="card">
      <div class="table-responsive" style="min-height: 350px">
        <table class="table table-bordered mb-0">
          <thead>
          <tr>
            <th>#SL</th>
            <th>Table</th>
            <th>Customer Details</th>
            <th>Order Number</th>
            <th>Order Date</th>
            <th class="text-right">Order Amount</th>
            <th>Invoice No</th>
            <th>Invoice Date</th>
            <th class="text-right">Invoice Amount</th>
            <th class="text-center">Action</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(item, i) in orderList.data" :key="i">
            <td>{{ (orderList.current_page - 1) * orderList.per_page + (i + 1) }}</td>
            <td>{{ item.table_name }}</td>
            <td> {{ item.party_name }} ({{ item.contact.company_roles && item.contact.company_roles[0] ? item.contact.company_roles[0].pivot.serial_no : ''}})</td>
            <td>
              <span>{{ item.bill_number }}</span>
            </td>
            <td> {{ item.date }}</td>
            <td class="text-right"> {{ item.bill_amount }}</td>
            <td>
              <span v-if="item.sale_master_id">{{ item.sale_master_bill_number }}</span>
            </td>
            <td>
              <span v-if="item.sale_master_id">{{ item.sale_master_date }}</span>
            </td>
            <td class="text-right">
              <span v-if="item.sale_master_id">{{ item.sale_master_total_amount }}</span>
            </td>
            <td>
              <div class="d-flex justify-content-center align-items-center action-btns">
                <button
                    v-if="!item.sale_master_id && item.status !== 'cancelled'"
                    class="btn btn-outline-primary btn-sm"
                    @click="$emit('editOrder', item)"
                >
                  <i class="fas fa-pencil-alt"></i>
                </button>
                <button
                    v-if="!item.sale_master_id && item.status !== 'cancelled'"
                    class="btn btn-outline-success btn-sm"
                    @click="$emit('createInvoice', item)"
                >
                  <i class="fas fa-receipt mr-1"></i> Create Final Bill
                </button>
                <button
                    v-if="!item.sale_master_id && item.status === 'active'"
                    class="btn btn-outline-danger btn-sm"
                    @click="updateStatus(item.id, 'cancelled')"
                >
                  <i class="fa fa-trash"></i>
                </button>
                <button
                    v-if="!item.sale_master_id && item.status === 'cancelled'"
                    class="btn btn-outline-success btn-sm"
                    @click="updateStatus(item.id, 'active')"
                >
                  Active
                </button>
              </div>
            </td>
          </tr>
          </tbody>
        </table>
        <p v-if="orderList.data && !orderList.data.length" class="text-center mt-5">No active orders</p>
      </div>
    </div>

    <div class="mt-2">
      <Pagination :offset="offset" @onPageChange="onPageChange" ref="pagination"/>
    </div>
  </div>
</template>

<script setup>
import {computed, inject, onMounted, ref} from 'vue';
import {useRoute} from 'vue-router';
import {useStore} from 'vuex';
import Pagination from '@/components/atom/Pagination';
import DateQuerySetter from '@/components/atom/DateQuerySetter';
import AsyncSelect from '@/components/molecule/input-field/AsyncSelect.vue';
import handlePurchase from '@/services/modules/purchase';
import handleContact from '@/services/modules/contact';
import handleDropdowns from '@/services/modules/dropdowns';
import {useAsyncDropdownHelper} from '@/services/utils/asyncDropdownHelper';

defineEmits(['addOrder', 'editOrder', 'createInvoice']);

const route = useRoute();
const store = useStore();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const {fetchSalesOrderList, fetchOrderStatus, updateOrderStatus} = handlePurchase();
const {fetchContactProfiles} = handleContact();
const {fetchRestaurantTablesDropdown} = handleDropdowns();
const {formatPatientLabel} = useAsyncDropdownHelper();

const companyId = computed(() => route.params.companyId);
const startDate = computed(() => route.query.start);
const endDate = computed(() => route.query.end);

const orderList = ref([]);
const offset = ref(20);
const contactProfileId = ref(null);
const selectedStatus = ref(null);
const selectedTableId = ref(null);
const orderStatuses = ref([]);

onMounted(() => {
  getOrders();
  getOrderStatus();
});

const getOrderStatus = async () => {
  const res = await fetchOrderStatus(`?company_id=${companyId.value}`);
  if (!res.status) {
    return;
  }
  orderStatuses.value = res.data.filter(item => ['pending', 'approved', 'cancelled', 'active'].includes(item.value));
};

const onClickGo = () => {
  getOrders();
};

const statusClass = (status) => {
  if (status === 'cancelled') return 'badge-danger';
  if (status === 'completed') return 'badge-secondary';
  return 'badge-success';
};

const actionLabel = (status) => {
  if (status === 'cancelled') {
    return 'cancel';
  }
  return 'activate';
};

const updateStatus = async (id, status) => {
  if (!confirm(`Are you sure you want to ${actionLabel(status)} this order?`)) {
    return;
  }
  const companyQuery = `?company_id=${companyId.value}&status=${status}`;
  try {
    const res = await updateOrderStatus(companyQuery, id);
    if (!res.status) {
      showError(res.message);
      return;
    }
    showSuccess(res.message);
    await getOrders();
  } catch (err) {
    showError('Failed to update order status');
  }
};

const serviceResourceType = 'App\\Models\\ServiceResource';

const getQuery = () => {
  let query = `?company_id=${companyId.value}`;
  query += '&is_hide_invoice=true';
  query += `&offset=${offset.value}`;
  if (startDate.value) query += `&start_date=${startDate.value}`;
  if (endDate.value) query += `&end_date=${endDate.value}`;
  if (contactProfileId.value) query += `&contact_profile_id=${contactProfileId.value}`;
  if (selectedStatus.value) query += `&status=${selectedStatus.value}`;
  if (selectedTableId.value) query += `&orderable_id=${selectedTableId.value}`;
  if (route.query.page) query += `&page=${route.query.page}`;
  return query;
};

const getOrders = async () => {
  try {
    const res = await fetchSalesOrderList(getQuery());
    if (!res.status) {
      orderList.value = [];
      store.commit('resetPagination');
      return;
    }
    orderList.value = res.date;
    setPagination(res.date);
  } catch (err) {
    showError('Failed to load orders');
  }
};

const setPagination = (data) => {
  if (!data.data.length) return;
  store.commit('setPaginationData', {
    records: data.total,
    perPage: data.per_page,
    currentPage: data.current_page,
  });
};

const onPageChange = async (page) => {
  route.query.page = page;
  await getOrders();
};

defineExpose({getOrders});
</script>

<style scoped>
.badge {
  text-transform: capitalize;
  padding: 5px 10px;
}

.action-btns {
  gap: 8px;
}

.action-btns .btn {
  white-space: nowrap;
}
</style>
